import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ChevronLeft } from "lucide-react";

const H2 = ({ children }) => <h2 className="font-display text-2xl text-stone-900 mb-4">{children}</h2>;
const P = ({ children }) => <p className="text-stone-700 leading-relaxed mb-3">{children}</p>;

export default function Datenschutz() {
  const [info, setInfo] = useState(null);

  useEffect(() => {
    api.get("/site-info").then((r) => setInfo(r.data)).catch(() => setInfo({}));
    document.title = "Datenschutzerklärung · Malermeister Santo Russo";
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute("content", "Datenschutzerklärung von Malermeister Santo Russo, Mainaschaff.");
    const robots = document.querySelector('meta[name="robots"]');
    if (robots) robots.setAttribute("content", "noindex, follow");
  }, []);

  if (!info) {
    return <div className="min-h-screen flex items-center justify-center bg-sand-100 font-display text-2xl text-stone-900">Laden…</div>;
  }

  return (
    <div data-testid="datenschutz-page" className="min-h-screen bg-sand-100 text-stone-900">
      <header className="border-b border-sand-300 bg-sand-100/85 backdrop-blur-xl sticky top-0 z-30">
        <div className="max-w-3xl mx-auto px-6 sm:px-12 h-20 flex items-center justify-between">
          <Link to="/" data-testid="datenschutz-back-link" className="inline-flex items-center gap-2 text-stone-700 hover:text-clay transition-colors">
            <ChevronLeft className="w-4 h-4" /> Zurück zur Webseite
          </Link>
          <span className="font-display text-xl text-stone-900 hidden sm:block">{info.company_name}</span>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 sm:px-12 py-16 sm:py-24">
        <p className="text-xs uppercase tracking-[0.25em] text-sage font-bold mb-4">Rechtliche Hinweise</p>
        <h1 className="font-display text-5xl sm:text-6xl text-stone-900 mb-12 leading-tight">Datenschutzerklärung</h1>

        <section className="mb-12">
          <H2>1. Datenschutz auf einen Blick</H2>
          <P>
            Die folgenden Hinweise geben einen einfachen Überblick darüber, was mit Ihren personenbezogenen Daten passiert,
            wenn Sie diese Website besuchen. Personenbezogene Daten sind alle Daten, mit denen Sie persönlich identifiziert
            werden können. Wir behandeln Ihre personenbezogenen Daten vertraulich und entsprechend den gesetzlichen
            Datenschutzvorschriften (DSGVO, BDSG) sowie dieser Datenschutzerklärung.
          </P>
          <P>
            Diese Website verwendet <strong className="text-stone-900">keine Cookies zu Analyse- oder Werbezwecken</strong> und
            setzt keine Tracking-Dienste ein. Alle Schriftarten werden lokal von unserem Server geladen – es findet keine
            Verbindung zu Drittanbieter-Schriftendiensten (z.&nbsp;B. Google Fonts) statt.
          </P>
        </section>

        <section className="mb-12">
          <H2>2. Verantwortliche Stelle</H2>
          <P>Verantwortlich für die Datenverarbeitung auf dieser Website ist:</P>
          <address className="not-italic text-stone-700 leading-relaxed mb-3">
            <strong className="text-stone-900">{info.company_name}</strong><br />
            Inhaber: Santo Russo<br />
            {info.address?.split(",").map((line, i, arr) => (
              <span key={i}>{line.trim()}{i < arr.length - 1 ? <br /> : ""}</span>
            ))}
          </address>
          <P>
            Telefon: <a href={`tel:${info.phone?.replace(/\s+/g, "")}`} className="text-clay hover:underline">{info.phone}</a>
            {info.email && (<><br />E-Mail: <a href={`mailto:${info.email}`} className="text-clay hover:underline">{info.email}</a></>)}
          </P>
          <P>
            Verantwortliche Stelle ist die natürliche oder juristische Person, die allein oder gemeinsam mit anderen über die
            Zwecke und Mittel der Verarbeitung von personenbezogenen Daten entscheidet.
          </P>
        </section>

        <section className="mb-12">
          <H2>3. Hosting und Server-Log-Dateien</H2>
          <P>
            Diese Website wird bei einem externen Dienstleister gehostet. Der Provider der Seiten erhebt und speichert
            automatisch Informationen in sogenannten Server-Log-Dateien, die Ihr Browser automatisch an uns übermittelt.
            Dies sind:
          </P>
          <ul className="list-disc pl-6 text-stone-700 leading-relaxed mb-3 space-y-1">
            <li>Browsertyp und Browserversion</li>
            <li>verwendetes Betriebssystem</li>
            <li>Referrer-URL</li>
            <li>Hostname des zugreifenden Rechners</li>
            <li>Uhrzeit der Serveranfrage</li>
            <li>IP-Adresse</li>
          </ul>
          <P>
            Eine Zusammenführung dieser Daten mit anderen Datenquellen wird nicht vorgenommen. Die Erfassung dieser Daten
            erfolgt auf Grundlage von Art. 6 Abs. 1 lit. f DSGVO. Der Websitebetreiber hat ein berechtigtes Interesse an der
            technisch fehlerfreien Darstellung und der Optimierung seiner Website – hierzu müssen die Server-Log-Files
            erfasst werden.
          </P>
        </section>

        <section className="mb-12">
          <H2>4. Kontaktformular</H2>
          <P>
            Wenn Sie uns per Kontaktformular Anfragen zukommen lassen, werden Ihre Angaben aus dem Anfrageformular
            (Name, E-Mail-Adresse, ggf. Telefonnummer, Betreff und Nachricht) inklusive der von Ihnen dort angegebenen
            Kontaktdaten zwecks Bearbeitung der Anfrage und für den Fall von Anschlussfragen bei uns gespeichert.
            Diese Daten geben wir nicht ohne Ihre Einwilligung weiter.
          </P>
          <P>
            Die Verarbeitung dieser Daten erfolgt auf Grundlage von Art. 6 Abs. 1 lit. b DSGVO, sofern Ihre Anfrage mit der
            Erfüllung eines Vertrags zusammenhängt oder zur Durchführung vorvertraglicher Maßnahmen erforderlich ist.
            In allen übrigen Fällen beruht die Verarbeitung auf unserem berechtigten Interesse an der effektiven Bearbeitung
            der an uns gerichteten Anfragen (Art. 6 Abs. 1 lit. f DSGVO) oder auf Ihrer Einwilligung (Art. 6 Abs. 1 lit. a DSGVO),
            sofern diese abgefragt wurde; die Einwilligung ist jederzeit widerrufbar.
          </P>
          <P>
            Die von Ihnen im Kontaktformular eingegebenen Daten verbleiben bei uns, bis Sie uns zur Löschung auffordern,
            Ihre Einwilligung zur Speicherung widerrufen oder der Zweck für die Datenspeicherung entfällt (z.&nbsp;B. nach
            abgeschlossener Bearbeitung Ihrer Anfrage). Zwingende gesetzliche Bestimmungen – insbesondere
            Aufbewahrungsfristen – bleiben unberührt.
          </P>
          <P>
            Zur Bearbeitung eingehender Anfragen kann eine interne Benachrichtigung per E-Mail oder Messenger-Dienst an den
            Inhaber erfolgen. Dabei werden ausschließlich die von Ihnen im Formular angegebenen Daten übermittelt.
          </P>
        </section>

        <section className="mb-12">
          <H2>5. Anfrage per E-Mail oder Telefon</H2>
          <P>
            Wenn Sie uns per E-Mail oder Telefon kontaktieren, wird Ihre Anfrage inklusive aller daraus hervorgehenden
            personenbezogenen Daten (Name, Anfrage) zum Zwecke der Bearbeitung Ihres Anliegens bei uns gespeichert und
            verarbeitet. Diese Daten geben wir nicht ohne Ihre Einwilligung weiter. Rechtsgrundlagen sind Art. 6 Abs. 1
            lit. b bzw. lit. f DSGVO.
          </P>
        </section>

        <section className="mb-12">
          <H2>6. SSL- bzw. TLS-Verschlüsselung</H2>
          <P>
            Diese Seite nutzt aus Sicherheitsgründen und zum Schutz der Übertragung vertraulicher Inhalte, wie zum Beispiel
            Anfragen, die Sie an uns als Seitenbetreiber senden, eine SSL- bzw. TLS-Verschlüsselung. Eine verschlüsselte
            Verbindung erkennen Sie daran, dass die Adresszeile des Browsers von „http://" auf „https://" wechselt und an
            dem Schloss-Symbol in Ihrer Browserzeile. Wenn die Verschlüsselung aktiviert ist, können die Daten, die Sie an
            uns übermitteln, nicht von Dritten mitgelesen werden.
          </P>
        </section>

        <section className="mb-12">
          <H2>7. Cookies und lokale Speicherung</H2>
          <P>
            Diese Website verwendet keine Cookies zu Analyse-, Marketing- oder Trackingzwecken. Lediglich im
            passwortgeschützten Verwaltungsbereich wird ein technisch notwendiges Sitzungs-Token im lokalen Speicher
            (localStorage) des Browsers abgelegt, um die Anmeldung aufrechtzuerhalten. Dieses betrifft ausschließlich den
            Betreiber der Website und keine Besucher.
          </P>
        </section>

        <section className="mb-12">
          <H2>8. Webschriftarten (lokal gehostet)</H2>
          <P>
            Diese Seite nutzt zur einheitlichen Darstellung von Schriftarten sogenannte Webfonts. Diese sind lokal auf
            unserem Server installiert. Eine Verbindung zu Servern von Drittanbietern (z.&nbsp;B. Google) findet dabei
            nicht statt.
          </P>
        </section>

        <section className="mb-12">
          <H2>9. Ihre Rechte als betroffene Person</H2>
          <P>Sie haben im Rahmen der geltenden gesetzlichen Bestimmungen jederzeit das Recht auf:</P>
          <ul className="list-disc pl-6 text-stone-700 leading-relaxed mb-3 space-y-1">
            <li>unentgeltliche Auskunft über Ihre gespeicherten personenbezogenen Daten, deren Herkunft und Empfänger und den Zweck der Datenverarbeitung (Art. 15 DSGVO)</li>
            <li>Berichtigung unrichtiger Daten (Art. 16 DSGVO)</li>
            <li>Löschung Ihrer Daten (Art. 17 DSGVO)</li>
            <li>Einschränkung der Verarbeitung (Art. 18 DSGVO)</li>
            <li>Datenübertragbarkeit (Art. 20 DSGVO)</li>
            <li>Widerspruch gegen die Verarbeitung (Art. 21 DSGVO)</li>
            <li>Widerruf einer erteilten Einwilligung mit Wirkung für die Zukunft (Art. 7 Abs. 3 DSGVO)</li>
          </ul>
          <P>
            Hierzu sowie zu weiteren Fragen zum Thema Datenschutz können Sie sich jederzeit an uns wenden
            (Kontaktdaten siehe Abschnitt 2).
          </P>
        </section>

        <section className="mb-12">
          <H2>10. Beschwerderecht bei der Aufsichtsbehörde</H2>
          <P>
            Im Falle von Verstößen gegen die DSGVO steht den Betroffenen ein Beschwerderecht bei einer Aufsichtsbehörde zu,
            insbesondere in dem Mitgliedstaat ihres gewöhnlichen Aufenthalts, ihres Arbeitsplatzes oder des Orts des
            mutmaßlichen Verstoßes. Für Bayern zuständig ist:
          </P>
          <P>
            Bayerisches Landesamt für Datenschutzaufsicht (BayLDA)<br />
            Promenade 18, 91522 Ansbach<br />
            <a href="https://www.lda.bayern.de" target="_blank" rel="noopener noreferrer" className="text-clay hover:underline">www.lda.bayern.de</a>
          </P>
        </section>

        <section>
          <H2>11. Aktualität dieser Datenschutzerklärung</H2>
          <P>
            Wir behalten uns vor, diese Datenschutzerklärung anzupassen, damit sie stets den aktuellen rechtlichen
            Anforderungen entspricht oder um Änderungen unserer Leistungen umzusetzen.
          </P>
          <P><em className="text-stone-600">Stand: Juni 2026</em></P>
        </section>
      </main>

      <footer className="border-t border-sand-300 py-8">
        <div className="max-w-3xl mx-auto px-6 sm:px-12 flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-sm text-stone-600">
          <p>© {new Date().getFullYear()} {info.company_name}. Alle Rechte vorbehalten.</p>
          <div className="flex gap-6">
            <Link to="/impressum" className="hover:text-clay transition-colors">Impressum</Link>
            <Link to="/" className="hover:text-clay transition-colors">Zurück zur Startseite</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
